import HeadTags from 'ui/views/layouts/BaseLayout/PageTitle';
import React, { useEffect } from 'react';
import styles from './styles.scss';
import MaxWidth, { maxWidths } from 'ui/views/containers/MaxWidth';
import Breadcrumb from './Breadcrumb';
import { contentSpacing, halfSpacing } from 'ui/theme/themeConstants';
import { bluePlanetTheme } from 'ui/theme';
import styled from '@emotion/styled';
import { ICompany } from 'types/company';
import CompanyHeaderMobile from 'ui/views/layouts/Mobile/CompanyHeaderMobile';
import { useMobileHeader } from 'domain/shared/Navigation';
import ScrollableBody from '../BaseLayout/ScrollableBody';
import { scrollToTop } from 'util/scroll';

interface Props {
  company: ICompany;
  title: string;
  documentTitle?: string;
  children: React.ReactNode;
  showCompanyNameBelowHeaderOnMobile?: boolean;
  /** Hand the viewport height to the content so it can own its own scroll (see ScrollableBody). */
  fillHeight?: boolean;
}

const Content = styled.div<{ fillHeight?: boolean }>`
  margin: ${contentSpacing};
  flex-grow: 1;
  ${bluePlanetTheme.breakpoints.down('md')} {
    margin: ${halfSpacing};
  }
  ${bluePlanetTheme.breakpoints.down('sm')} {
    margin: 0;
  }

  ${props =>
    props.fillHeight
      ? `
    margin: 0;
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
  `
      : ''}
`;

const InnerContent = styled.div<{ fillHeight?: boolean }>`
  margin: 0 auto;
  max-width: ${maxWidths.xl};

  ${props =>
    props.fillHeight
      ? `
    flex: 1;
    min-height: 0;
    display: flex;
    flex-direction: column;
    width: 100%;
  `
      : ''}
`;

export function Main({ children, fillHeight }: { children: React.ReactNode; fillHeight?: boolean }) {
  return (
    <Content fillHeight={fillHeight}>
      <InnerContent fillHeight={fillHeight}>{children}</InnerContent>
    </Content>
  );
}

export default function CompanyPage(props: Props) {
  const { company, showCompanyNameBelowHeaderOnMobile, fillHeight } = props;

  useEffect(() => {
    scrollToTop();
  }, []);

  const documentTitle = `${props.documentTitle ?? props.title}${company?.name ? ` – ${company?.name}` : ''}`;

  useMobileHeader(props.title);

  return (
    <>
      <HeadTags title={documentTitle} />
      <ScrollableBody accountForMobileHeader className={styles.bottomMobileNavigationOffset} fillHeight={fillHeight}>
        {showCompanyNameBelowHeaderOnMobile && <CompanyHeaderMobile company={company} />}
        <div className={styles.bannerHeader} style={{ flexShrink: 0 }}>
          <MaxWidth className={styles.innerBannerHeader} width="xl">
            {company && <Breadcrumb title={props.title} company={company} />}
          </MaxWidth>
        </div>
        {props.children}
      </ScrollableBody>
    </>
  );
}
